import type { SearchResponse } from "../types";
import { formatCents, formatDateShort } from "../utils/format";

interface Props {
  results: SearchResponse;
}

/**
 * Headline value-prop banner: which city is cheapest and how much it saves
 * versus the most expensive tour stop. This is the whole reason the product
 * exists, so it sits above the results list.
 */
export function DealSummary({ results }: Props) {
  const shows = results.shows;
  if (shows.length === 0) return null;

  const totals = shows.map((s) => s.packages.cheapest.total_cents);
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  const cheapest =
    shows.find((s) => s.packages.cheapest.total_cents === min) ?? shows[0];
  const savings = max - min;

  return (
    <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-brand-700 to-brand-900 p-5 text-white shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-200">
            Cheapest city to see {results.artist.name}
          </p>
          <p className="mt-1 text-2xl font-extrabold">
            {cheapest.venue.city}, {cheapest.venue.state}
          </p>
          <p className="text-sm text-brand-100">
            {formatDateShort(cheapest.show_date)} · {cheapest.venue.name}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-extrabold">{formatCents(min)}</p>
          <p className="text-xs text-brand-200">all-in trip</p>
        </div>
      </div>

      {savings > 0 && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-medium">
          <span aria-hidden>💸</span>
          Save up to {formatCents(savings)} vs. the priciest city
        </div>
      )}
    </div>
  );
}
